import { useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Image as ImageIcon,
  FileText,
  User as UserIcon,
  MapPin,
  Calendar as CalendarIcon,
  UserX,
  KeyRound,
  Upload,
  Trash2,
} from 'lucide-react';
import { contractService, appointmentService, erpDataService } from '../_shared/api';
import type {
  ApprovalStatus,
  Appointment,
  AppointmentStatus,
  Contract,
  ContractDocument,
  ContractStatus,
  DueDate,
  Plan,
  User,
  Vendedor,
} from '../_shared/types';
import ImageViewer from './ImageViewer';
import AppointmentModal from './AppointmentModal';
import { useModalKeys } from '../../../hooks/useModalKeys';
import { useAuth } from '../../../context/AuthContext';
import { userRoles } from '../../../utils/roles';

/**
 * ContractDetailModal — porte do web novo (prospect_nova_versao/web) pro
 * painel SAAS, com paridade de informação (Fase 31):
 *
 *   - Header com nome do cliente + badges de status
 *   - IDs do ERP (contrato/cliente/pré-cadastro/lançado por/em)
 *   - Dados PF/PJ + endereço (campos vazios ocultos)
 *   - Seção Contrato: POP, plano (com valor), vencimento, vendedor,
 *     condição comercial, observação e login gerado
 *   - Documentos em grade com miniatura + upload + exclusão
 *   - Agendamento vinculado + fluxo de encaixe (aprovar/reprovar)
 *   - Aprovar / Reprovar / Desistência
 */
interface Props {
  contractId: string | null;
  onClose: () => void;
  onChanged?: () => void;
}

const STATUS_LABEL: Record<ContractStatus, { label: string; cls: string }> = {
  draft: { label: 'Rascunho', cls: 'bg-active text-fg' },
  pending: { label: 'Pendente', cls: 'bg-warning-solid text-white' },
  sent: { label: 'Enviado', cls: 'bg-primary-solid text-white' },
  confirmed: { label: 'Confirmado', cls: 'bg-success-solid text-white' },
  error: { label: 'Erro', cls: 'bg-danger-solid text-white' },
};

const APPROVAL_LABEL: Record<ApprovalStatus, { label: string; cls: string }> = {
  pending: { label: 'Pendente', cls: 'bg-warning-solid text-white' },
  approved: { label: 'Aprovado', cls: 'bg-success-solid text-white' },
  rejected: { label: 'Reprovado', cls: 'bg-danger-solid text-white' },
  declined: { label: 'Declinado', cls: 'bg-warning-solid-hover text-white' },
};

const APPT_STATUS_LABEL: Record<AppointmentStatus, string> = {
  scheduled: 'Agendado',
  rescheduled: 'Reagendado',
  completed: 'Concluído',
  cancelled: 'Cancelado',
  pending_squeeze: 'Encaixe pendente',
  rejected_squeeze: 'Encaixe reprovado',
};

const PF_FIELDS: Array<[string, string]> = [
  ['nome', 'Nome'],
  ['cpfcnpj', 'CPF'],
  ['rg', 'RG'],
  ['rg_emissor', 'Emissor'],
  ['sexo', 'Sexo'],
  ['estadocivil', 'Estado civil'],
  ['nomemae', 'Nome da mãe'],
  ['nomepai', 'Nome do pai'],
  ['profissao', 'Profissão'],
  ['nacionalidade', 'Nacionalidade'],
  ['naturalidade', 'Naturalidade'],
  ['email', 'E-mail'],
  ['celular', 'Celular'],
  ['datanasc', 'Data de nascimento'],
];

const PJ_FIELDS: Array<[string, string]> = [
  ['nome', 'Razão social'],
  ['cpfcnpj', 'CNPJ'],
  ['nomefantasia', 'Nome fantasia'],
  ['datanasc', 'Data de fundação'],
  ['respempresa', 'Responsável'],
  ['respcpf', 'CPF do responsável'],
  ['email', 'E-mail'],
  ['celular', 'Celular'],
];

const ADDRESS_FIELDS: Array<[string, string]> = [
  ['logradouro', 'Logradouro'],
  ['numero', 'Número'],
  ['complemento', 'Complemento'],
  ['bairro', 'Bairro'],
  ['cidade', 'Cidade'],
  ['cep', 'CEP'],
  ['uf', 'UF'],
  ['pais', 'País'],
  ['pontoreferencia', 'Ponto de referência'],
];

export default function ContractDetailModal({ contractId, onClose, onChanged }: Props) {
  const { user } = useAuth();
  // Aprovar/Reprovar/Agendar: admin + gestão de vendas (supervisor) —
  // operator é o legado full-access. Encaixe é mais restrito (admin).
  const roles = userRoles(user);
  const canReview = roles.some(
    (r) => r === 'admin' || r === 'operator' || r === 'gestao_vendas',
  );
  const canReviewSqueeze = roles.some((r) => r === 'admin' || r === 'operator');

  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(false);
  const [submittingDecision, setSubmittingDecision] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [docs, setDocs] = useState<ContractDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [viewerDocId, setViewerDocId] = useState<string | null>(null);
  // Upload de documento (Fase 31 — paridade com o web novo)
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDescricao, setUploadDescricao] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Lookups do ERP pra resolver plano/vencimento/vendedor (Fase 31)
  const [plans, setPlans] = useState<Plan[]>([]);
  const [dueDates, setDueDates] = useState<DueDate[]>([]);
  const [vendedores, setVendedores] = useState<Vendedor[]>([]);
  // Agendamento vinculado + fluxo de encaixe (Fase 27)
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [squeezeReviewing, setSqueezeReviewing] = useState(false);
  const [showSqueezeReject, setShowSqueezeReject] = useState(false);
  const [squeezeRejectReason, setSqueezeRejectReason] = useState('');
  // Desistência do cliente (Fase 27)
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  // Agendar/reagendar instalação (Fase 31 — paridade com o web novo)
  const [showAppointment, setShowAppointment] = useState(false);

  useEffect(() => {
    if (!contractId) {
      setContract(null);
      setDocs([]);
      setAppointment(null);
      setShowRejectForm(false);
      setRejectReason('');
      setShowDeclineForm(false);
      setDeclineReason('');
      setShowSqueezeReject(false);
      setSqueezeRejectReason('');
      setUploadFile(null);
      setUploadDescricao('');
      setShowAppointment(false);
      return;
    }
    setLoading(true);
    contractService
      .get(contractId)
      .then(setContract)
      .catch(() => alert('Não foi possível carregar o contrato.'))
      .finally(() => setLoading(false));

    setLoadingDocs(true);
    contractService
      .listDocuments(contractId)
      .then(setDocs)
      .catch(() => setDocs([]))
      .finally(() => setLoadingDocs(false));

    appointmentService
      .getByContract(contractId)
      .then(setAppointment)
      .catch(() => setAppointment(null));

    // Lookups do ERP — resolvem plano/vencimento/vendedor da seção Contrato
    Promise.all([
      erpDataService.getPlans().catch(() => [] as Plan[]),
      erpDataService.getDueDates().catch(() => [] as DueDate[]),
      erpDataService.getVendedores().catch(() => [] as Vendedor[]),
    ]).then(([p, d, v]) => {
      setPlans(p);
      setDueDates(d);
      setVendedores(v);
    });
  }, [contractId]);

  async function reloadDocs(): Promise<void> {
    if (!contractId) return;
    setLoadingDocs(true);
    try {
      setDocs(await contractService.listDocuments(contractId));
    } catch {
      /* mantém a lista atual */
    } finally {
      setLoadingDocs(false);
    }
  }

  async function handleUploadDoc(): Promise<void> {
    if (!contract || !uploadFile) return;
    if (!uploadDescricao.trim()) {
      alert('Descrição é obrigatória.');
      return;
    }
    setUploadingDoc(true);
    try {
      await contractService.uploadDocument(contract.id, uploadFile, uploadDescricao.trim());
      setUploadFile(null);
      setUploadDescricao('');
      await reloadDocs();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Erro ao enviar: ' + (e.response?.data?.message ?? e.message));
    } finally {
      setUploadingDoc(false);
    }
  }

  async function handleDeleteDoc(docId: string): Promise<void> {
    if (!contract || !confirm('Remover este documento?')) return;
    try {
      await contractService.deleteDocument(contract.id, docId);
      await reloadDocs();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Erro ao remover: ' + (e.response?.data?.message ?? e.message));
    }
  }

  // Labels resolvidos dos lookups do ERP (Fase 31 — seção Contrato)
  const renderData = (contract?.data ?? {}) as Record<string, unknown>;

  const planoLabel = useMemo(() => {
    const id = Number(renderData.plano_id ?? 0);
    if (!id) return null;
    const p = plans.find((x) => x.erpId === id);
    return p ? `${p.descricao} — R$ ${p.valor.toFixed(2)}` : `#${id}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderData.plano_id, plans]);

  const vencimentoLabel = useMemo(() => {
    const id = Number(renderData.vencimento_id ?? 0);
    if (!id) return null;
    const d = dueDates.find((x) => x.erpId === id);
    return d ? `Dia ${d.dia}` : `#${id}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderData.vencimento_id, dueDates]);

  const vendedorLabel = useMemo(() => {
    const id = Number(renderData.vendedor_id ?? 0);
    if (!id) return contract?.vendedorNome ?? null;
    const v = vendedores.find((x) => x.erpId === id);
    return v ? v.nome : (contract?.vendedorNome ?? `#${id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderData.vendedor_id, vendedores, contract?.vendedorNome]);

  // Quem lançou o contrato (userId populado; guard contra null — typeof
  // null === 'object')
  const launcher =
    contract && contract.userId && typeof contract.userId === 'object'
      ? (contract.userId as User)
      : null;

  async function handleApprove(): Promise<void> {
    if (!contract) return;
    if (!confirm('Aprovar este contrato?')) return;
    setSubmittingDecision(true);
    try {
      const { contract: updated } = await contractService.approve(contract.id);
      setContract(updated);
      onChanged?.();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Erro ao aprovar: ' + (e.response?.data?.message ?? e.message));
    } finally {
      setSubmittingDecision(false);
    }
  }

  async function handleReject(): Promise<void> {
    if (!contract || !rejectReason.trim()) return;
    setSubmittingDecision(true);
    try {
      const { contract: updated } = await contractService.reject(contract.id, rejectReason.trim());
      setContract(updated);
      setShowRejectForm(false);
      setRejectReason('');
      onChanged?.();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Erro ao reprovar: ' + (e.response?.data?.message ?? e.message));
    } finally {
      setSubmittingDecision(false);
    }
  }

  async function handleApproveSqueeze(): Promise<void> {
    if (!appointment) return;
    if (!confirm('Aprovar o encaixe? O chamado será criado no ERP e o vendedor notificado.')) return;
    setSqueezeReviewing(true);
    try {
      const { appointment: updated } = await appointmentService.approveSqueeze(appointment.id);
      setAppointment(updated);
      onChanged?.();
      alert('Encaixe aprovado e enviado ao ERP.');
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Erro ao aprovar encaixe: ' + (e.response?.data?.message ?? e.message));
    } finally {
      setSqueezeReviewing(false);
    }
  }

  async function handleRejectSqueeze(): Promise<void> {
    if (!appointment || !squeezeRejectReason.trim()) return;
    setSqueezeReviewing(true);
    try {
      const { appointment: updated } = await appointmentService.rejectSqueeze(
        appointment.id,
        squeezeRejectReason.trim(),
      );
      setAppointment(updated);
      setShowSqueezeReject(false);
      setSqueezeRejectReason('');
      onChanged?.();
      alert('Encaixe reprovado. O vendedor foi notificado.');
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Erro ao reprovar encaixe: ' + (e.response?.data?.message ?? e.message));
    } finally {
      setSqueezeReviewing(false);
    }
  }

  async function handleDecline(): Promise<void> {
    if (!contract || !declineReason.trim()) return;
    setSubmittingDecision(true);
    try {
      const { contract: updated } = await contractService.decline(
        contract.id,
        declineReason.trim(),
      );
      setContract(updated);
      setShowDeclineForm(false);
      setDeclineReason('');
      onChanged?.();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Erro ao declinar: ' + (e.response?.data?.message ?? e.message));
    } finally {
      setSubmittingDecision(false);
    }
  }

  // Esc fecha; clique fora NÃO fecha (Fase 30)
  useModalKeys(onClose, undefined, !!contractId);

  if (!contractId) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center sm:justify-center sm:p-4">
      <div className="bg-surface w-full sm:max-w-2xl sm:rounded-2xl border-t sm:border border-line flex flex-col max-h-[100vh] sm:max-h-[92vh]">
        {/* Header — nome do cliente + badges (paridade com o web novo) */}
        <div className="px-5 py-4 border-b border-line flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-fg text-lg truncate">
              {contract?.clienteNome || 'Detalhes do contrato'}
            </h2>
            {contract && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span
                  className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${APPROVAL_LABEL[contract.approvalStatus].cls}`}
                >
                  {APPROVAL_LABEL[contract.approvalStatus].label}
                </span>
                {contract.approvalStatus !== 'declined' && (
                  <span
                    className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_LABEL[contract.status].cls}`}
                  >
                    {STATUS_LABEL[contract.status].label}
                  </span>
                )}
                <span className="text-xs text-muted">
                  {contract.tipo === 'F' ? 'PF' : 'PJ'}
                </span>
                {contract.erpContratoId && (
                  <span className="text-xs text-muted">#{contract.erpContratoId}</span>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-fg-2 hover:text-fg flex-shrink-0">
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 size={32} className="animate-spin text-primary" />
            </div>
          ) : !contract ? (
            <p className="text-center text-muted">Contrato não encontrado.</p>
          ) : (
            <>
              {contract.errorMessage && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-danger-soft/20 border border-danger-line/50 text-sm text-danger">
                  <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                  <span>{contract.errorMessage}</span>
                </div>
              )}

              {contract.approvalStatus === 'rejected' && contract.rejectionReason && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-warning-soft/20 border border-warning-line/50 text-sm text-warning">
                  <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Motivo da reprovação:</strong> {contract.rejectionReason}
                  </div>
                </div>
              )}

              {contract.approvalStatus === 'declined' && contract.declineReason && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-warning-soft/20 border border-warning-line/50 text-sm text-warning">
                  <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Motivo da desistência:</strong> {contract.declineReason}
                  </div>
                </div>
              )}

              {/* IDs do ERP (paridade com o web novo) */}
              <Section icon={<KeyRound size={14} />} title="IDs do ERP">
                <FieldRow label="Contrato" value={contract.erpContratoId ?? '—'} />
                <FieldRow label="Cliente" value={contract.erpClienteId ?? '—'} />
                <FieldRow label="Pré-cadastro" value={contract.erpPrecadastroId ?? '—'} />
                {launcher && (
                  <FieldRow label="Lançado por" value={`${launcher.name} (${launcher.email})`} />
                )}
                <FieldRow
                  label="Lançado em"
                  value={new Date(contract.createdAt).toLocaleString('pt-BR')}
                />
                {contract.sentAt && (
                  <FieldRow
                    label="Enviado em"
                    value={new Date(contract.sentAt).toLocaleString('pt-BR')}
                  />
                )}
              </Section>

              {/* Agendamento vinculado (Fase 27) */}
              {appointment && (
                <Section icon={<CalendarIcon size={14} />} title="Agendamento">
                  <FieldRow
                    label="Data"
                    value={`${new Date(appointment.data).toLocaleDateString('pt-BR')} — ${appointment.periodo}`}
                  />
                  <FieldRow
                    label="Status"
                    value={APPT_STATUS_LABEL[appointment.status] ?? appointment.status}
                  />
                  {appointment.status === 'rejected_squeeze' &&
                    appointment.squeezeRejectionReason && (
                      <FieldRow label="Motivo" value={appointment.squeezeRejectionReason} />
                    )}

                  {appointment.status === 'pending_squeeze' && canReviewSqueeze && (
                    <div className="mt-2 rounded-lg border border-info-line/50 bg-info-soft/20 p-3 space-y-2">
                      <p className="text-xs text-info font-medium">
                        Solicitação de encaixe pendente — o vendedor pediu vaga extra
                        além do limite da cidade.
                      </p>
                      {!showSqueezeReject ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowSqueezeReject(true)}
                            disabled={squeezeReviewing}
                            className="flex-1 h-9 bg-danger-solid/20 hover:bg-danger-solid/30 border border-danger-line/50 rounded-lg text-danger text-xs font-bold"
                          >
                            Reprovar encaixe
                          </button>
                          <button
                            onClick={() => void handleApproveSqueeze()}
                            disabled={squeezeReviewing}
                            className="flex-1 h-9 bg-success-solid hover:bg-success-solid-hover disabled:bg-active rounded-lg text-white text-xs font-bold flex items-center justify-center gap-1.5"
                          >
                            {squeezeReviewing ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <>
                                <CheckCircle2 size={13} />
                                Aprovar encaixe
                              </>
                            )}
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <textarea
                            value={squeezeRejectReason}
                            onChange={(e) => setSqueezeRejectReason(e.target.value)}
                            placeholder="Motivo da reprovação do encaixe (visível ao vendedor)…"
                            rows={2}
                            className="w-full bg-canvas border border-line rounded-lg p-2.5 text-fg text-xs placeholder:text-muted outline-none focus:border-primary resize-none"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setShowSqueezeReject(false);
                                setSqueezeRejectReason('');
                              }}
                              disabled={squeezeReviewing}
                              className="flex-1 h-9 bg-canvas hover:bg-hover rounded-lg text-fg-2 text-xs font-medium"
                            >
                              Cancelar
                            </button>
                            <button
                              onClick={() => void handleRejectSqueeze()}
                              disabled={squeezeReviewing || !squeezeRejectReason.trim()}
                              className="flex-1 h-9 bg-danger-solid hover:bg-danger-solid-hover disabled:bg-active rounded-lg text-white text-xs font-bold flex items-center justify-center"
                            >
                              {squeezeReviewing ? (
                                <Loader2 size={13} className="animate-spin" />
                              ) : (
                                'Confirmar reprovação'
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Section>
              )}

              {/* Dados pessoais */}
              {contract.data && (
                <Section
                  icon={<UserIcon size={14} />}
                  title={contract.tipo === 'F' ? 'Dados pessoais' : 'Dados da empresa'}
                >
                  {(contract.tipo === 'F' ? PF_FIELDS : PJ_FIELDS).map(([key, label]) => {
                    const v = renderData[key];
                    if ((!v && v !== 0) || v === '') return null;
                    return <FieldRow key={key} label={label} value={String(v)} />;
                  })}
                </Section>
              )}

              {/* Endereço */}
              {contract.data && (
                <Section icon={<MapPin size={14} />} title="Endereço de instalação">
                  {ADDRESS_FIELDS.map(([key, label]) => {
                    const v = renderData[key];
                    if ((!v && v !== 0) || v === '') return null;
                    return <FieldRow key={key} label={label} value={String(v)} />;
                  })}
                </Section>
              )}

              {/* Contrato — plano/vencimento/vendedor/condição/observação/login */}
              <Section icon={<FileText size={14} />} title="Contrato">
                <FieldRow label="POP" value={contract.popNome ?? '—'} />
                {planoLabel && <FieldRow label="Plano" value={planoLabel} />}
                {vencimentoLabel && <FieldRow label="Vencimento" value={vencimentoLabel} />}
                {vendedorLabel && <FieldRow label="Vendedor" value={vendedorLabel} />}
                {renderData.servicodesc != null && renderData.servicodesc !== '' && (
                  <FieldRow label="Condição comercial" value={String(renderData.servicodesc)} />
                )}
                {renderData.observacao != null && renderData.observacao !== '' && (
                  <FieldRow label="Observação" value={String(renderData.observacao)} />
                )}
                {renderData.login != null && renderData.login !== '' && (
                  <FieldRow label="Login gerado" value={String(renderData.login)} />
                )}
              </Section>

              {/* Documentos — grade com miniaturas + upload (paridade web novo) */}
              <Section icon={<ImageIcon size={14} />} title={`Documentos (${docs.length})`}>
                {loadingDocs ? (
                  <Loader2 size={16} className="animate-spin text-muted mx-auto" />
                ) : docs.length === 0 ? (
                  <p className="text-xs text-muted mb-3">Nenhum documento anexado.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
                    {docs.map((d) => (
                      <div
                        key={d.id}
                        className="relative group aspect-square rounded-xl overflow-hidden bg-hover"
                      >
                        <button
                          onClick={() => setViewerDocId(d.id)}
                          className="absolute inset-0 w-full h-full"
                          title={d.descricao || d.fileName}
                        >
                          {d.thumb ? (
                            <img src={d.thumb} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ImageIcon size={22} className="text-faint" />
                            </div>
                          )}
                          {d.descricao && (
                            <div className="absolute bottom-0 inset-x-0 bg-black/60 px-1.5 py-1 text-left">
                              <p className="text-white text-[10px] truncate">{d.descricao}</p>
                            </div>
                          )}
                        </button>
                        <button
                          onClick={() => void handleDeleteDoc(d.id)}
                          className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 hover:bg-danger-solid flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                          title="Remover"
                        >
                          <Trash2 size={12} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Anexar novo */}
                <div className="bg-hover/60 rounded-xl p-3">
                  <p className="text-xs uppercase text-fg-2 font-bold mb-2">Anexar novo</p>
                  <div className="flex flex-col sm:flex-row gap-2 items-stretch">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="h-10 px-3 bg-active hover:bg-faint text-white rounded-lg text-sm flex items-center justify-center gap-2"
                    >
                      <Upload size={14} />
                      {uploadFile ? uploadFile.name.slice(0, 20) : 'Escolher arquivo'}
                    </button>
                    <input
                      value={uploadDescricao}
                      onChange={(e) => setUploadDescricao(e.target.value)}
                      placeholder="Descrição"
                      className="flex-1 h-10 px-3 bg-canvas border border-active rounded-lg text-white text-sm outline-none focus:border-primary placeholder:text-muted"
                    />
                    <button
                      onClick={() => void handleUploadDoc()}
                      disabled={!uploadFile || !uploadDescricao.trim() || uploadingDoc}
                      className="h-10 px-4 bg-primary-solid hover:bg-primary-solid-hover disabled:bg-primary-soft rounded-lg text-white text-sm font-bold flex items-center justify-center gap-2"
                    >
                      {uploadingDoc ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <span>Enviar</span>
                      )}
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                  />
                </div>
              </Section>

              {/* Agendar/reagendar instalação — aprovado + já no ERP */}
              {canReview &&
                contract.approvalStatus === 'approved' &&
                !!contract.erpContratoId && (
                  <button
                    onClick={() => setShowAppointment(true)}
                    className="w-full h-12 rounded-2xl bg-primary-solid hover:bg-primary-solid-hover text-white font-bold flex items-center justify-center gap-2"
                  >
                    <CalendarIcon size={18} />
                    {appointment && appointment.status !== 'rejected_squeeze'
                      ? 'Reagendar instalação'
                      : 'Agendar instalação'}
                  </button>
                )}
            </>
          )}
        </div>

        {/* Footer com ações de aprovação/desistência (admin/gestão de vendas) */}
        {contract &&
          canReview &&
          contract.approvalStatus === 'pending' &&
          !showRejectForm &&
          !showDeclineForm && (
          <div className="px-5 py-3 border-t border-line flex gap-2">
            <button
              onClick={() => setShowRejectForm(true)}
              disabled={submittingDecision}
              className="flex-1 h-11 bg-danger-solid/20 hover:bg-danger-solid/30 border border-danger-line/50 rounded-xl text-danger font-medium flex items-center justify-center gap-2"
            >
              <XCircle size={16} />
              Reprovar
            </button>
            <button
              onClick={() => setShowDeclineForm(true)}
              disabled={submittingDecision}
              className="flex-1 h-11 bg-warning-solid/20 hover:bg-warning-solid/30 border border-warning-line/50 rounded-xl text-warning font-medium flex items-center justify-center gap-2"
            >
              <UserX size={16} />
              Declinou
            </button>
            <button
              onClick={() => void handleApprove()}
              disabled={submittingDecision}
              className="flex-1 h-11 bg-success-solid hover:bg-success-solid-hover disabled:bg-active rounded-xl text-white font-bold flex items-center justify-center gap-2"
            >
              {submittingDecision ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  <CheckCircle2 size={16} />
                  Aprovar
                </>
              )}
            </button>
          </div>
        )}

        {/* Declinou solo: contratos já decididos (aprovado/reprovado) também
            podem ser marcados como desistência */}
        {contract &&
          contract.approvalStatus !== 'pending' &&
          contract.approvalStatus !== 'declined' &&
          !showDeclineForm && (
          <div className="px-5 py-3 border-t border-line">
            <button
              onClick={() => setShowDeclineForm(true)}
              disabled={submittingDecision}
              className="w-full h-11 bg-warning-solid/20 hover:bg-warning-solid/30 border border-warning-line/50 rounded-xl text-warning font-medium flex items-center justify-center gap-2"
            >
              <UserX size={16} />
              Cliente desistiu
            </button>
          </div>
        )}

        {showDeclineForm && (
          <div className="px-5 py-3 border-t border-line space-y-2">
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Motivo da desistência (visível ao vendedor)…"
              rows={3}
              className="w-full bg-canvas border border-line rounded-xl p-3 text-fg text-sm placeholder:text-muted outline-none focus:border-primary resize-none"
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowDeclineForm(false);
                  setDeclineReason('');
                }}
                disabled={submittingDecision}
                className="flex-1 h-11 bg-canvas hover:bg-hover rounded-xl text-fg-2 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={() => void handleDecline()}
                disabled={submittingDecision || !declineReason.trim()}
                className="flex-1 h-11 bg-warning-solid hover:bg-warning-solid-hover disabled:bg-active rounded-xl text-white font-bold flex items-center justify-center gap-2"
              >
                {submittingDecision ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  'Confirmar desistência'
                )}
              </button>
            </div>
          </div>
        )}

        {showRejectForm && (
          <div className="px-5 py-3 border-t border-line space-y-2">
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Motivo da reprovação (visível ao vendedor)…"
              rows={3}
              className="w-full bg-canvas border border-line rounded-xl p-3 text-fg text-sm placeholder:text-muted outline-none focus:border-primary resize-none"
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowRejectForm(false);
                  setRejectReason('');
                }}
                disabled={submittingDecision}
                className="flex-1 h-11 bg-canvas hover:bg-hover rounded-xl text-fg-2 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={() => void handleReject()}
                disabled={submittingDecision || !rejectReason.trim()}
                className="flex-1 h-11 bg-danger-solid hover:bg-danger-solid-hover disabled:bg-active rounded-xl text-white font-bold flex items-center justify-center gap-2"
              >
                {submittingDecision ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  'Confirmar reprovação'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Image viewer */}
        {contractId && (
          <ImageViewer
            contractId={contractId}
            docId={viewerDocId}
            onClose={() => setViewerDocId(null)}
          />
        )}

        {/* Agendamento de instalação (Fase 31) */}
        {showAppointment && contract && (
          <AppointmentModal
            contract={contract}
            onClose={() => setShowAppointment(false)}
            onScheduled={() => {
              setShowAppointment(false);
              // Recarrega o agendamento vinculado pra refletir a nova data
              appointmentService
                .getByContract(contract.id)
                .then(setAppointment)
                .catch(() => undefined);
              onChanged?.();
            }}
          />
        )}
      </div>
    </div>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-primary">{icon}</span>
        <h3 className="text-xs font-bold uppercase tracking-wider text-primary">{title}</h3>
      </div>
      <div className="bg-canvas border border-line rounded-xl p-3 space-y-1.5">
        {children}
      </div>
    </section>
  );
}

function FieldRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="text-xs text-muted uppercase tracking-wide w-1/3 flex-shrink-0">
        {label}
      </span>
      <span className="text-fg break-all">{value || '—'}</span>
    </div>
  );
}
